import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  ContentCard,
  Row,
  Button,
  Chip,
  Alert,
  Radio,
} from "@narmi/design_system";
import { Dialog, FileUploader, useNotificationContext } from "cerulean";
import ApiHttp from "byzantine/src/ApiHttp";
import { modules } from "byzantine";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import HeaderWithSteps from "../../HeaderWithSteps";
import Container from "../../Container";
import {
  extractFilenameFromResponse,
  triggerFileDownload,
} from "../../DownloadTransactionsDialog/transactionDownloadHelpers";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import { useACHImportContext } from "../ACHImportContext";
import { useDomesticWireImportContext } from "../WireFileImport";
import {
  MAX_ACH_IMPORT_FILE_SIZE_BYTES,
  CSV_EXTENSIONS,
  ALLOWED_EXTENSIONS,
} from "./fileUploadConstants";

const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot === -1) return "";
  return fileName.slice(lastDot).toLowerCase();
};

const isNachaValidationError = (message: string): boolean =>
  message.startsWith("Invalid NACHA file:");

const ACH_CSV_FIELDS: {
  labelKey: string;
  required: boolean;
}[] = [
  {
    labelKey: "file-upload-csv-field-recipient-name",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-account",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-routing",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-amount",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-date",
    required: false,
  },
  {
    labelKey: "file-upload-csv-field-action",
    required: false,
  },
  {
    labelKey: "file-upload-csv-field-description",
    required: false,
  },
  {
    labelKey: "file-upload-csv-field-recipient-account-type",
    required: false,
  },
];
const WIRE_CSV_FIELDS: {
  labelKey: string;
  required: boolean;
}[] = [
  {
    labelKey: "file-upload-csv-field-recipient-name",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-account",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-routing",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-street-address",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-city",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-state",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-zip-code",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-wire-reason",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-amount",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-memo",
    required: true,
  },
];

export const ACH_IMPORT_SAMPLE_CSV = [
  [
    "Recipient Name",
    "Account Number",
    "Routing Number",
    "Amount",
    "Date",
    "Action",
    "Description",
    "Recipient Account Type",
  ],
  [
    "Acme Supplies Co.",
    "123456789",
    "021000021",
    "1250.00",
    "06/15/2026",
    "send",
    "Monthly office supply order",
    "checking",
  ],
]
  .map((row) => row.join(","))
  .join("\n");

const ACH_IMPORT_SAMPLE_CSV_FILENAME = "sample_payment_import.csv";

const useACHImportSampleCsvUrl = (): string | undefined => {
  const [sampleCsvUrl, setSampleCsvUrl] = useState<string | undefined>();

  useEffect(() => {
    const objectUrl = URL.createObjectURL(new Blob([ACH_IMPORT_SAMPLE_CSV], { type: "text/csv" }));
    setSampleCsvUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, []);

  return sampleCsvUrl;
};

const LearnMoreModal = ({
  isOpen,
  onClose,
  isWires,
}: {
  isOpen: boolean;
  onClose: () => void;
  isWires: boolean;
}) => {
  const { l10n } = useLocalization();
  const wireReasons = modules.wireTemplates.useWireReasons();

  const csvFields = isWires ? WIRE_CSV_FIELDS : ACH_CSV_FIELDS;
  return (
    <Dialog
      isOpen={isOpen}
      title={l10n.getString("file-upload-learn-more-title")}
      onUserDismiss={onClose}
      headerStyle="bordered"
      width="627px"
    >
      <div className="margin--top--l">
        <span className="fontWeight--bold">
          {l10n.getString(
            isWires
              ? "file-upload-wire-csv-requirements"
              : "file-upload-csv-requirements"
          )}
        </span>
      </div>
      <div className="margin--top--s">
        {csvFields.map(({ labelKey, required }) => (
          <div
            key={labelKey}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "var(--space-m) 0",
              borderBottom: "1px solid var(--color-lightGrey)",
            }}
          >
            <span className="fontSize--s">{l10n.getString(labelKey)}</span>

            {isWires ? (
              <Chip
                kind={required ? "primary" : "info"}
                label={l10n.getString(
                  required
                    ? "file-upload-csv-required"
                    : "file-upload-csv-optional"
                )}
              />
            ) : (
              <Chip
                kind={required ? "info" : "secondary"}
                label={l10n.getString(
                  required
                    ? "file-upload-csv-required"
                    : "file-upload-csv-optional"
                )}
              />
            )}
          </div>
        ))}
      </div>
      {isWires ? (
        <>
          <div className="margin--top--l">
            <span className="fontSize--s">
              {l10n.getString("file-upload-wire-csv-note")}
            </span>
          </div>
          <p
            className="fontColor--secondary fontSize--xs fontWeight--bold padding--top--xs margin--top--l margin--bottom--m"
            style={{ borderTop: "1px solid var(--color-lightGrey)" }}
          >
            {l10n.getString("file-upload-accepted-wire-reasons")}
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "var(--space-xs)",
            }}
          >
            {wireReasons.map((reason) => (
              <Chip
                key={reason}
                kind="secondary"
                label={l10n.getString(reason)}
                hasBorder={true}
              />
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="margin--top--l">
            <span className="fontSize--s">
              {l10n.getString("file-upload-csv-note")}
            </span>
          </div>
          <div
            className="margin--top--l"
            style={{
              borderTop: "1px solid var(--color-lightGrey)",
              paddingTop: "var(--space-l)",
            }}
          >
            <span className="fontWeight--bold">
              {l10n.getString("file-upload-nacha-requirements")}
            </span>
            <div className="margin--top--s">
              <span className="fontSize--s">
                {l10n.getString("file-upload-nacha-note")}
              </span>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
};

const getUploadErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object") {
    const apiError = error as { error?: unknown };
    if (typeof apiError.error === "string") return apiError.error;
  }
  return fallback;
};

const FileUploadScreen = () => {
  const { l10n } = useLocalization();
  const sampleCsvUrl = useACHImportSampleCsvUrl();
  const features = useUserFeatures();
  const { sendNotification } = useNotificationContext();
  const {
    nachaResponse,
    setNachaResponse,
    setCsvResponse,
    clearImport: clearACHImport,
    importType,
  } = useACHImportContext();
  const { setCsvResponse: setWireCsvResponse, clearImport: clearWireImport } =
    useDomesticWireImportContext();
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [modeError, setModeError] = useState<string | null>(null);
  const navigate = useNavigate();

  const achEnabled = Boolean(
    features.ach_file_import && features.bulk_payments
  );
  const wireEnabled = Boolean(features.domestic_wire_file_import);
  const bothEnabled = achEnabled && wireEnabled;

  const getDefaultMode = (): "ach" | "wire" | null => {
    if (bothEnabled) return null;
    if (wireEnabled) return "wire";
    return "ach";
  };

  const [importMode, setImportMode] = useState<"ach" | "wire" | null>(
    getDefaultMode
  );

  const handleModeChange = (mode: "ach" | "wire") => {
    setImportMode(mode);
    setModeError(null);
    setUploadedFileName(null);
    setUploadComplete(false);
    setFileError(null);
    clearWireImport();
    clearACHImport();
  };

  const sendRequest = useCallback(
    async (file: File) => {
      setFileError(null);
      const extension = getFileExtension(file.name);

      const allowedExts =
        importMode === "wire" ? CSV_EXTENSIONS : ALLOWED_EXTENSIONS;

      if (!allowedExts.includes(extension)) {
        const errorKey =
          importMode === "wire"
            ? "file-upload-error-invalid-extension-csv-only"
            : "file-upload-error-invalid-extension";
        sendNotification({
          type: "negative",
          text: l10n.getString(errorKey),
        });
        throw new Error("Invalid file extension");
      }

      const formData = new FormData();
      formData.append("file", file);

      if (importMode === "wire") {
        try {
          const response = await ApiHttp.fetch(
            "wire_import/csv/import/",
            { method: "POST" },
            formData
          );
          setWireCsvResponse(response as API.WireImport.UploadCSV.Response);
          setUploadComplete(true);
        } catch (error) {
          const errorText = getUploadErrorMessage(
            error,
            l10n.getString("file-upload-error-generic")
          );
          setFileError(errorText);
          throw errorText;
        }
        return;
      }

      const isCSV = CSV_EXTENSIONS.includes(extension);
      const endpoint = isCSV
        ? "ach_import/csv/import/"
        : "ach_import/nacha/import/";

      try {
        const response = await ApiHttp.fetch(
          endpoint,
          { method: "POST" },
          formData
        );

        if (isCSV) {
          setCsvResponse(response as API.ACHImport.CSVUploadResponse);
        } else {
          setNachaResponse(response as API.ACHImport.PaymentsResponse);
        }

        setUploadComplete(true);
      } catch (error) {
        const uploadErrorText = getUploadErrorMessage(
          error,
          l10n.getString("file-upload-error-generic")
        );
        const errorText = isNachaValidationError(uploadErrorText)
          ? l10n.getString("file-upload-error-nacha-validation")
          : uploadErrorText;
        setFileError(errorText);
        throw errorText;
      }
    },
    [
      importMode,
      l10n,
      sendNotification,
      setCsvResponse,
      setNachaResponse,
      setWireCsvResponse,
    ]
  );

  if (!achEnabled && !wireEnabled) {
    window.location.assign(`/${PAYMENT_ACTIVITY_ROUTE}`);
    return null;
  }

  const handleClickNext = () => {
    if (bothEnabled && !importMode) {
      setModeError(l10n.getString("file-upload-error-select-type"));
      return;
    }
    if (!uploadComplete) {
      setFileError(l10n.getString("file-upload-error-no-file"));
      return;
    }
    if (importMode === "wire") {
      navigate("wire/map-columns");
      return;
    }
    if (importType === "csv") {
      navigate("map-columns");
      return;
    }
    if (importType === "nacha") {
      const hasNewRecipients = nachaResponse?.rows.some(
        (row) => row.non_recipient_destination !== null
      );
      if (hasNewRecipients) {
        navigate("review-recipients");
      } else {
        navigate("additional-details");
      }
    }
  };

  const supportingTextKey =
    importMode === "wire"
      ? "file-upload-supporting-text-csv-only"
      : "file-upload-supporting-text";

  const handleWireSampleDownload = useCallback(async () => {
    try {
      const response = await ApiHttp.fetch("wire_import/csv/sample/", {
        method: "GET",
        shouldGetBlob: true,
      });

      const filename = extractFilenameFromResponse(
        response.headers["content-disposition"],
        "csv"
      );
      triggerFileDownload(response.data, filename);
    } catch (error) {
      sendNotification({
        type: "negative",
        text: getUploadErrorMessage(
          error,
          l10n.getString("file-upload-error-generic")
        ),
      });
    }
  }, [l10n, sendNotification]);

  return (
    <Container maxWidth="640px">
      <HeaderWithSteps
        useDisplayFont
        headerText={l10n.getString(
          "file-upload-screen-combined-header",
        )}
      />
      {bothEnabled && (
        <div className="margin--top--xl">
          <span className="fontSize--l fontWeight--bold">
            {l10n.getString("file-upload-type-selector-label")}
          </span>
          <div
            className="margin--top--l"
            style={{ display: "flex", gap: "var(--space-s)" }}
          >
            <div style={{ flex: 1 }}>
              <Radio
                kind="input-card"
                name="importMode"
                value="ach"
                checked={importMode === "ach"}
                onCheck={() => handleModeChange("ach")}
              >
                <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
                  {l10n.getString("file-upload-type-ach-title")}
                </h4>
              </Radio>
            </div>
            <div style={{ flex: 1 }}>
              <Radio
                kind="input-card"
                name="importMode"
                value="wire"
                checked={importMode === "wire"}
                onCheck={() => handleModeChange("wire")}
              >
                <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
                  {l10n.getString("file-upload-type-wire-title")}
                </h4>
              </Radio>
            </div>
          </div>
          {modeError && (
            <div className="margin--top--s">
              <span className="fontSize--s fontColor--error">{modeError}</span>
            </div>
          )}
        </div>
      )}
      {importMode && (
        <div className="margin--top--l">
          <ContentCard kind="bordered" paddingSize="l">
            <span className="fontSize--l fontWeight--bold">
              {l10n.getString("file-upload-section-label")}
            </span>
            <div className="margin--top--l">
              <style>
                {`
                .ach-file-uploader #file-uploader-label {
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  min-height: 180px;
                  gap: var(--space-s);
                }
                .ach-file-uploader #file-uploader-label .narmi-icon-upload {
                  padding-right: 0;
                  font-size: var(--font-size-xl);
                }
                .ach-file-uploader #file-uploader-label .upload-button .fontColor--secondary {
                  display: block;
                  text-align: center;
                  margin-top: var(--space-xs);
                }
                .ach-file-uploader > .nds-input-error {
                  margin-top: var(--space-xs);
                }
              `}
              </style>
              <div className="ach-file-uploader">
                {importType === "nacha" &&
                  uploadComplete &&
                  nachaResponse?.warnings?.map((warningType) => (
                    <div
                      key={warningType}
                      className="margin--bottom--l"
                      data-testid={`file-upload-warning-${warningType}`}
                    >
                      <Alert isActive kind="warn" isDismissable={false}>
                        {l10n.getString(
                          `column-mapping-warning-${warningType.replace(
                            /_/g,
                            "-"
                          )}`,
                          nachaResponse.warning_context?.[warningType] ?? {}
                        )}
                      </Alert>
                    </div>
                  ))}
                <FileUploader
                  key={importMode}
                  sendRequest={sendRequest}
                  shouldSuppressErrorNotification={(error: unknown) =>
                    typeof error === "string" &&
                    error ===
                      l10n.getString("file-upload-error-nacha-validation")
                  }
                  onChange={(fileName: string | null) => {
                    setUploadedFileName(fileName);
                    if (!fileName) {
                      setUploadComplete(false);
                    }
                  }}
                  value={uploadedFileName}
                  error={fileError}
                  expand
                  maxFileUploadSizeBytes={MAX_ACH_IMPORT_FILE_SIZE_BYTES}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  messages={
                    {
                      errorNoFile: l10n.getString("file-upload-error-no-file"),
                      errorMultipleUnsupported: l10n.getString(
                        "file-upload-error-multiple-unsupported"
                      ),
                      errorUnsupportedType: l10n.getString(
                        "file-upload-error-invalid-extension"
                      ),
                      errorFailedExceedsSize: l10n.getString(
                        "file-upload-error-exceeds-size"
                      ),
                      errorFailed: l10n.getString("file-upload-error-generic"),
                      label: l10n.getString("file-upload-label"),
                      maxSizeLabel: l10n.getString(supportingTextKey),
                    } as any
                  }
                />
              </div>
            </div>
            <div
                className="margin--top--l"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
            {importMode === "ach" ? (
                <a
                  href={sampleCsvUrl}
                  download={ACH_IMPORT_SAMPLE_CSV_FILENAME}
                  className="fontSize--s fontWeight--semibold"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "var(--space-xs)",
                    border: "1px solid var(--color-lightGrey)",
                    borderRadius: "12px",
                    padding: "var(--space-xs) var(--space-s)",
                    color: "var(--theme-secondary)",
                    textDecoration: "none",
                  }}
                >
                  <span className="narmi-icon-download" />
                  {l10n.getString("file-upload-csv-sample")}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={handleWireSampleDownload}
                  className="fontSize--s fontWeight--semibold"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "var(--space-xs)",
                    border: "1px solid var(--color-lightGrey)",
                    borderRadius: "12px",
                    padding: "var(--space-xs) var(--space-s)",
                    background: "transparent",
                    cursor: "pointer",
                    color: "var(--theme-secondary)",
                    textDecoration: "none",
                  }}
                >
                  <span className="narmi-icon-download" />
                  {l10n.getString("file-upload-csv-sample-wire")}
                </button>
              )}
              <span
                className="fontSize--s"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-xs)",
                }}
              >
                <span className="narmi-icon-help-circle" />
                {l10n.getString("file-upload-learn-more-prompt")}{" "}
                <button
                  type="button"
                  onClick={() => setIsLearnMoreOpen(true)}
                  className="fontWeight--semibold"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: "inherit",
                    color: "var(--theme-secondary)",
                  }}
                >
                  {l10n.getString("file-upload-learn-more")}
                </button>
              </span>
            </div>
          </ContentCard>
        </div>
      )}
      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="plain"
              label={l10n.getString("button-cancel")}
              onClick={() => {
                window.location.assign(`/${PAYMENT_ACTIVITY_ROUTE}/bulk`);
              }}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleClickNext}
            />
          </Row.Item>
        </Row>
      </div>
      <LearnMoreModal
        isOpen={isLearnMoreOpen}
        onClose={() => setIsLearnMoreOpen(false)}
        isWires={importMode === "wire"}
      />
    </Container>
  );
};

export default FileUploadScreen;
